import { ThunkAction } from 'redux-thunk';
import * as Actions from './actions';
import { AnyAction } from 'redux';
import { AppState } from '../../store';
import moment from 'moment';
import { homehubUserApiPath, v2BaseApiPath } from '../../../config/baseConfig';
import { ItemValueKey } from '../../../config/item-value-key';
import { ItemValueKeySuffix } from '../../../enums/common/item-value-key-suffix';
import { SumamoruAdditionalServices, guideAppType } from '../property/types';
import { isContractorOnly, isOccupantOnly } from '../invitation/operations';
import { ApplicantType, getApplicantText } from '../invitation/types';

export interface ActivationContext {
  organizationId: string;
  usageInfoId: string;
  applicantType: ApplicantType;
}

/** 対象の入居情報が Inviting/Sending でないときに homehub が返すコード。= 既にアクティベート済み */
const ALREADY_ACTIVATED_CODE = 'not-found-any-space-usage-and-owners';

/**
 * 入居者本人の操作なので、takuty の完了API経由ではなく本人のアクセストークンで homehub を直接呼ぶ
 */
const activateOccupant = async (param: {
  organizationId: string;
  peopleId: string;
  usageId: string;
  accessToken: string;
}): Promise<boolean> => {
  const query = new URLSearchParams({
    organization_id: param.organizationId,
    people_id: param.peopleId
  });
  const res = await fetch(`${homehubUserApiPath}/organizations/people/activate?${query.toString()}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-api-key': param.accessToken
    },
    body: JSON.stringify({ accept_space_usage_ids: [param.usageId] }),
    mode: 'cors'
  });
  if (res.status === 200) {
    return true;
  }
  // 既にアクティベート済みなら成功扱い。招待URLで再訪して再実行したときも同じ応答になる
  if (res.status === 400) {
    const json = await res.json().catch(() => undefined);
    return json && json.code === ALREADY_ACTIVATED_CODE;
  }
  return false;
};

/**
 * 申込フォームの入力値（item-definitions の valueKey で保存されている）を取り出す。
 * 画面に表示されていない項目は送らない（v1 と同じく `${valueKey}IsDisplayed === false` で判定する）
 */
const getDisplayedValue = (inputValues: any, valueKey: string): string | undefined => {
  if (inputValues[valueKey + ItemValueKeySuffix.IsDisplayed] === false) {
    return undefined;
  }
  return getInputValue(inputValues, valueKey);
};

/** 未入力・`Invalid date` を除いた入力値。表示されているかは見ない */
const getInputValue = (inputValues: any, valueKey: string): string | undefined => {
  const value = inputValues[valueKey];
  if (value === undefined || value === null || value === '' || value === 'Invalid date') {
    return undefined;
  }
  return value;
};

export const submitParamater = (
  activation: ActivationContext
): ThunkAction<Promise<void>, AppState, {}, AnyAction> => async (dispatch, getState) => {
  try {
    dispatch(Actions.submitParamater());

    const formParam: any = getState().applyForm.parameters;
    const email = getState().user.email;
    const access_token = getState().user.access_token;
    const refresh_token = getState().user.refresh_token;
    const user_id = getState().user.user_id;
    const propParam = getState().property.property;
    const phoneNumber = getState().user.phoneNumber;
    const method = 'POST';

    const isContractorUser = isContractorOnly(activation.applicantType);
    const isOccupantUser = isOccupantOnly(activation.applicantType);

    // 契約者のみはBKPアカウント連携を行わないため access_token を持たない。
    // 認証ヘッダを付けられないので アカウント登録もスキップする
    const headers: { [key: string]: string } = isContractorUser
      ? { Accept: 'application/json', 'Content-Type': 'application/json' }
      : {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-api-key': access_token
        };
    let body = '';
    let bkpPersonaId: string | undefined;

    if (!isContractorUser) {
      const homeHubUserEndpoint = `${v2BaseApiPath}/accounts`;
      body = JSON.stringify({
        bkpUserId: user_id,
        // 初回登録時は入力された名前で登録される
        name: `${formParam[ItemValueKey.CustomerLastName]} ${formParam[ItemValueKey.CustomerFirstName]}`,
        ...(email ? { email } : {}),
        ...(phoneNumber ? { phoneNumber } : {})
      });
      const accountRes = await fetch(homeHubUserEndpoint, {
        method,
        headers,
        body,
        mode: 'cors'
      });

      const accountSucceeded = accountRes.status === 200 || accountRes.status === 201;

      if (!accountSucceeded) {
        dispatch(Actions.failedToSubmitParamater(accountRes.status === 401 ? 'unauthorized' : 'internal'));
        return;
      }

      // BKPログイン応答にも personaId は含まれるが、状態をクッキーから復元する経路では
      // 持ち回れないため homehub に返させる
      const accountJson = await accountRes.json().catch(() => undefined);
      bkpPersonaId = accountJson && accountJson.personaId;
      if (!bkpPersonaId) {
        dispatch(Actions.failedToSubmitParamater('internal'));
        return;
      }
    }

    const endpoint = `${v2BaseApiPath}/application/create`;

    // 申込者種別ごとに、共通項目へ contractor / occupant を足す
    // 兼任: 両方 / 入居者: occupant のみ / 契約者: contractor のみ
    const applicationTarget = {
      ...(isContractorUser ? {} : { refresh_token: refresh_token }),
      ...(isContractorUser
        ? {
            contract_info_id: propParam.contractInfoId,
            people_id: propParam.peopleId
          }
        : {}),
      applicant_type: getApplicantText(activation.applicantType),
      organization_id: activation.organizationId,
      property_id: propParam.allSpaceId,
      contract_id: propParam.contractId,
      property_no: propParam.sumamoruCode,
      // 画面の判定用に XXX-XXXX に揃えているので、API には数字7桁で送る
      postal_code: propParam.postCode.replace(/-/g, ''),
      prefecture: propParam.prefecture,
      city: propParam.city,
      after_city: propParam.afterCity,
      address1: propParam.prefecture + propParam.city + propParam.afterCity,
      address2: propParam.buildingName + propParam.propertyName,
      building_name: propParam.buildingName,
      property_name: propParam.propertyName,
      ...(isContractorUser ? {} : { move_plan_date: moment(propParam.occupyScheduledDate).format('YYYY-MM-DD') }),
      ...(propParam.viewnStartDate ? { viewn_start_date: propParam.viewnStartDate } : {}),
      has_electric_contract: propParam.hasElectricContract,
      sumamoru_project_type: propParam.sumamoruProjectType,
      install_app_name: guideAppType(propParam.sumamoruInstallAppTypes),
      sumamoru_additional_services:
        propParam.sumamoruAdditionalServices &&
        propParam.sumamoruAdditionalServices.filter((service): service is SumamoruAdditionalServices =>
          Object.values(SumamoruAdditionalServices).includes(service as SumamoruAdditionalServices)
        )
    };

    const customerPhoneNumber = formParam[ItemValueKey.CustomerPhoneNumber];
    // 氏名・連絡先は契約者 / 入居者で共通の入力欄を使う
    const applicantProfile = {
      family_name: formParam[ItemValueKey.CustomerLastName],
      first_name: formParam[ItemValueKey.CustomerFirstName],
      family_name_kana: formParam[ItemValueKey.CustomerLastNameKana],
      first_name_kana: formParam[ItemValueKey.CustomerFirstNameKana],
      email: formParam[ItemValueKey.CustomerEmail],
      phone_number: customerPhoneNumber,
      phone_number_after_move: customerPhoneNumber
    };

    const contractor = isOccupantUser ? undefined : toContractor(applicantProfile, formParam, propParam);

    const gender = formParam[ItemValueKey.CustomerGender];
    const occupant = {
      ...applicantProfile,
      // 生年月日・性別は入居者の属性なので occupant にだけ載せる
      birthday: toBirthday(formParam[ItemValueKey.CustomerBirthDay]),
      // 画面の選択値は 1:男性 / 2:女性 / 3:無回答、API は 0 / 1 / 2
      sex: gender ? parseInt(gender, 10) - 1 : undefined,
      bkp_user_id: user_id,
      usage_info_id: propParam.usageInfoId,
      people_id: propParam.peopleId,
      bkp_persona_id: bkpPersonaId
    };

    body = JSON.stringify({
      ...applicationTarget,
      ...(isOccupantUser ? {} : { contractor }),
      ...(isContractorUser ? {} : { occupant })
    });

    await fetch(endpoint, {
      method,
      headers,
      body,
      mode: 'cors'
    })
      .then(async res => {
        if (res.status === 200) {
          // 契約者のみはアクティベート対象が無い (カギを持たない)
          if (isContractorUser) {
            dispatch(Actions.successToSubmitParamater());
            return;
          }
          // 招待URLのクエリ由来で入居者経路なら必ず入るが、Property 上は任意なので絞り込む
          if (!propParam.peopleId) {
            dispatch(Actions.failedToSubmitParamater('internal'));
            return;
          }
          const activated = await activateOccupant({
            organizationId: activation.organizationId,
            peopleId: propParam.peopleId,
            usageId: propParam.usageId,
            accessToken: access_token
          });
          dispatch(activated ? Actions.successToSubmitParamater() : Actions.failedToSubmitParamater('internal'));
        } else if (res.status === 401) {
          dispatch(Actions.failedToSubmitParamater('unauthorized'));
        } else if (res.status === 409) {
          dispatch(Actions.failedToSubmitParamater('conflict'));
        } else {
          dispatch(Actions.failedToSubmitParamater('internal'));
        }
      })
      .catch(error => {
        console.error(error);
        dispatch(Actions.failedToSubmitParamater('internal'));
      });
  } catch (error) {
    console.error(error);
    dispatch(Actions.failedToSubmitParamater('internal'));
  }
};

/**
 * 契約者ブロック。電気・ガスの需給契約に必要な項目を CDE の入力値から組み立てる。
 * 生年月日・性別は入居者の属性なので載せない（契約者のみの申込みで送ると 400 になる）
 */
const toContractor = (applicantProfile: object, formParam: any, propParam: AppState['property']['property']) => {
  if (!propParam.hasElectricContract) {
    return applicantProfile;
  }
  const useGasPlanSelection = getInputValue(formParam, ItemValueKey.UseGasPlanSelection);
  // 画面の選択値は 0:ガスもセットで申込む / 1:申込まない、API の use_og_gas は 1:利用 / 0:利用しない
  const useGas = useGasPlanSelection === '0';
  const paymentMethod = getDisplayedValue(formParam, ItemValueKey.PaymentMethodSelection);
  const gasValue = (valueKey: string) => (useGas ? getDisplayedValue(formParam, valueKey) : undefined);
  // 0:ベーシックガス / 1:ゆかぽかガス
  const gasBasePlan = useGas ? getInputValue(formParam, ItemValueKey.GasBasePlanSelection) : undefined;

  return {
    ...applicantProfile,
    pay_method: paymentMethod !== undefined ? parseInt(paymentMethod, 10) : undefined,
    use_og_gas: useGasPlanSelection !== undefined ? (useGas ? 1 : 0) : undefined,
    start_power_date: getDisplayedValue(formParam, ItemValueKey.ElectricityStartDate),
    start_gas_date: gasValue(ItemValueKey.GasStartDate),
    electricity_base_plan_selection: getDisplayedValue(formParam, ItemValueKey.ElectricityBasePlanSelection),
    gas_base_plan_selection: gasBasePlan,
    // 0:ガスセット割引（ガスもセットで申込む） / 1:なし
    electricity_option_plan_selection: useGasPlanSelection,
    // 0:電気セット割引（ベーシックガス） / 1:なし（ゆかぽかガス）
    gas_option_plan_selection: gasBasePlan,
    gas_start_time_selection: gasValue(ItemValueKey.GasStartTimeSelection),
    witness_gas_start: gasValue(ItemValueKey.WitnessGasStart),
    witness_name_gas_start: gasValue(ItemValueKey.WitnessNameGasStart),
    phone_number_for_gas_start: gasValue(ItemValueKey.PhoneNumberForGasStart)
  };
};

/** 画面の生年月日（YYYY/M/D）を API の YYYYMMDD に揃える */
const toBirthday = (birthday?: string): string | undefined => {
  if (!birthday) {
    return undefined;
  }
  const date = moment(birthday, ['YYYY/MM/DD', 'YYYY/M/D', 'YYYYMMDD'], true);
  return date.isValid() ? date.format('YYYYMMDD') : undefined;
};
